// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

import { isVirtualDevice } from '../../common/virtualDevice'

interface DeviceLike {
  id: string | number
  // The `numeric` start_time column is serialized as a string by the API on
  // some paths and a number on others — coerce, don't type-check.
  start_time?: number | string | null
}

/** Sensor activation (epoch ms) if the device has a usable start_time, else null. */
function deviceStartMs(startTime: number | string | null | undefined): number | null {
  const n = typeof startTime === 'number' ? startTime : Number(startTime)
  return Number.isFinite(n) && n > 0 ? n * 1000 : null
}

/**
 * Resolve the start (epoch ms) of the pet-detail date window from the selected
 * device and the pet's device list. Returns null only when the pet has NO
 * device with a usable sensor-activation time (caller then uses the 14-day
 * fallback).
 *
 * Why this exists — the page-jump bug (pet 284836 / Boone, 2026-06):
 * A pet with one dated device (start_time set) and one freshly-activated device
 * (start_time still null/0) made the window flip between the device span and
 * the 14-day fallback as the selection settled. Each flip changed the report's
 * stats AND its height, so the page scroll "jumped" continuously.
 *
 * Two rules keep the window STABLE so it cannot oscillate:
 *  1. A selected single device with no own start_time falls back to the
 *     pet-wide earliest start — NOT the 14-day default. So when the pet has any
 *     dated device, both "states" resolve to the same span and there is nothing
 *     to flip between.
 *  2. The result is a primitive number, so a consumer can memoize the window on
 *     it and keep a stable object identity even when the devices array ref
 *     churns on refetch (which previously cascaded into a re-render storm).
 */
/**
 * Cache-stable window END (epoch ms): "now" rounded UP to the next 5-minute
 * boundary.
 *
 * Why (2026-07-10 request storm): every mount of the pet-detail panel stamped
 * a fresh `new Date()` as the window end, so each end bound — and therefore
 * every emrs/activities/metrics react-query KEY derived from it — was unique
 * per mount. Under intermittent 429s the page flapped, each remount re-fired
 * the panel's full query set (~10 heavy calls) with keys that could never hit
 * cache, which kept the rate limiter tripped, which kept the page flapping —
 * a self-sustaining ~400-700 requests/minute storm.
 *
 * Rounding UP makes every mount within the same 5-minute bucket produce
 * IDENTICAL query keys, so remounts are served from the react-query cache with
 * ZERO network — the loop has no fuel. The bound sits slightly in the future,
 * so the newest readings are always inside the window (nothing is trimmed).
 */
export const WINDOW_END_BUCKET_MS = 5 * 60 * 1000
export function stableWindowEndMs(nowMs: number): number {
  return Math.ceil(nowMs / WINDOW_END_BUCKET_MS) * WINDOW_END_BUCKET_MS
}

/**
 * Cache-stable 14-day fallback START (epoch ms), floored to the same 5-minute
 * bucket so a pet with no usable device start_time also reuses cache across
 * remounts.
 */
export function stableFallbackStartMs(nowMs: number): number {
  const raw = nowMs - 14 * 24 * 60 * 60 * 1000
  return Math.floor(raw / WINDOW_END_BUCKET_MS) * WINDOW_END_BUCKET_MS
}

export function resolveWindowStartMs(
  selectedDeviceId: string | number | null | undefined,
  petDevices: DeviceLike[],
): number | null {
  const starts = petDevices
    .map((d) => deviceStartMs(d.start_time))
    .filter((ms): ms is number => ms != null)
  const petEarliestMs = starts.length > 0 ? Math.min(...starts) : null

  // Virtual "All Devices": span every device → pet-wide earliest.
  if (isVirtualDevice(selectedDeviceId ?? undefined)) return petEarliestMs

  // A specific device: prefer its own activation, else fall back to the
  // pet-wide earliest (NOT the 14-day default) so a start_time-less device
  // cannot collapse and flip the window.
  if (selectedDeviceId != null) {
    const dev = petDevices.find((d) => String(d.id) === String(selectedDeviceId))
    return deviceStartMs(dev?.start_time) ?? petEarliestMs
  }

  // No selection yet → pet-wide earliest if known.
  return petEarliestMs
}
